import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MailOpen, Trash2, Phone, RotateCcw, CheckCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { PageTitle } from "./Layout";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

const TABS = [
  ["all", "Tümü"],
  ["unread", "Okunmamış"],
  ["read", "Okunmuş"],
  ["trash", "Çöp Kutusu"],
];

export default function MessagesAdmin() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(null);
  const [sel, setSel] = useState(() => new Set());

  const { data = [] } = useQuery({
    queryKey: ["admin", "messages"],
    queryFn: () => api.get("/admin/messages").then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin", "messages"] });
    qc.invalidateQueries({ queryKey: ["admin", "stats"] });
  };

  const list = useMemo(() => {
    const base = data.map((m) => ({ ...m, deleted: !!m.deleted }));
    if (tab === "trash") return base.filter((m) => m.deleted);
    const live = base.filter((m) => !m.deleted);
    if (tab === "unread") return live.filter((m) => !m.read);
    if (tab === "read") return live.filter((m) => m.read);
    return live;
  }, [data, tab]);

  const changeTab = (k) => {
    setTab(k);
    setSel(new Set());
    setOpen(null);
  };

  const toggleSel = (id) => {
    setSel((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSel((prev) => (prev.size === list.length ? new Set() : new Set(list.map((m) => m.id))));
  };

  const bulk = async (action) => {
    const ids = Array.from(sel);
    if (!ids.length) return;
    const labels = { read: "okundu", unread: "okunmadı", trash: "çöpe", restore: "geri alındı", purge: "kalıcı silindi" };
    if ((action === "trash" && !window.confirm(`${ids.length} mesaj çöpe atılsın mı?`)) ||
        (action === "purge" && !window.confirm(`${ids.length} mesaj KALICI olarak silinecek. Emin misiniz?`))) return;
    try {
      const r = await api.post("/admin/messages/bulk", { ids, action });
      toast.success(`${r.data.affected} mesaj ${labels[action]}.`);
      setSel(new Set());
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const mark = async (m, read) => {
    try { await api.patch(`/admin/messages/${m.id}`, { read }); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (m) => {
    const trashed = m.deleted;
    const msg = trashed ? "Mesaj kalıcı olarak silinecek. Emin misiniz?" : "Mesaj çöpe atılsın mı?";
    if (!window.confirm(msg)) return;
    try { await api.delete(`/admin/messages/${m.id}`); setOpen(null); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const restoreOne = async (m) => {
    try {
      await api.post("/admin/messages/bulk", { ids: [m.id], action: "restore" });
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const emptyTrash = async () => {
    if (!window.confirm("Çöp kutusundaki tüm mesajlar kalıcı olarak silinecek. Emin misiniz?")) return;
    try {
      const r = await api.post("/admin/messages/trash/empty");
      toast.success(`${r.data.affected} mesaj silindi.`);
      setSel(new Set());
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const allSelected = list.length > 0 && sel.size === list.length;
  const anySelected = sel.size > 0;
  const inTrash = tab === "trash";
  const trashCount = useMemo(() => data.filter((m) => m.deleted).length, [data]);

  return (
    <>
      <PageTitle title="Mesajlar" sub="İletişim ve gönüllü formlarından gelenler." />

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map(([k, l]) => (
          <Button
            key={k}
            size="sm"
            variant={tab === k ? "default" : "outline"}
            onClick={() => changeTab(k)}
            data-testid={`messages-tab-${k}`}
          >
            {l}{k === "trash" && trashCount ? ` (${trashCount})` : ""}
          </Button>
        ))}
      </div>

      {/* Bulk action bar */}
      <div
        className="flex flex-wrap items-center gap-2 mb-3 p-2.5 bg-stone-50 border rounded-lg"
        data-testid="messages-bulk-bar"
      >
        <label className="flex items-center gap-2 text-sm pl-1 cursor-pointer select-none">
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleAll}
            data-testid="messages-select-all"
          />
          <span className="text-stone-600">
            {anySelected ? `${sel.size} seçildi` : "Tümünü seç"}
          </span>
        </label>
        <div className="flex-1" />
        {inTrash ? (
          <>
            <Button size="sm" variant="outline" disabled={!anySelected}
              onClick={() => bulk("restore")} data-testid="bulk-restore">
              <Undo2 size={14} className="mr-1" />Geri al
            </Button>
            <Button size="sm" variant="outline" disabled={!anySelected}
              onClick={() => bulk("purge")} data-testid="bulk-purge">
              <Trash2 size={14} className="mr-1 text-red-500" />Kalıcı sil
            </Button>
            <Button size="sm" variant="ghost" disabled={!trashCount}
              onClick={emptyTrash} data-testid="bulk-empty-trash">
              Çöpü boşalt
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" disabled={!anySelected}
              onClick={() => bulk("read")} data-testid="bulk-read">
              <CheckCheck size={14} className="mr-1" />Okundu yap
            </Button>
            <Button size="sm" variant="outline" disabled={!anySelected}
              onClick={() => bulk("unread")} data-testid="bulk-unread">
              <Mail size={14} className="mr-1" />Okunmadı yap
            </Button>
            <Button size="sm" variant="outline" disabled={!anySelected}
              onClick={() => bulk("trash")} data-testid="bulk-trash">
              <Trash2 size={14} className="mr-1 text-red-500" />Sil
            </Button>
          </>
        )}
      </div>

      <div className="bg-white border rounded-xl divide-y" data-testid="messages-list">
        {!list.length && <p className="p-6 text-sm text-stone-400">Mesaj yok.</p>}
        {list.map((m) => {
          const checked = sel.has(m.id);
          const isOpen = open === m.id;
          return (
            <div key={m.id} data-testid={`message-row-${m.id}`}>
              <div
                className={`flex items-center gap-3 px-3 sm:px-5 py-3.5 hover:bg-stone-50 ${checked ? "bg-amber-50/60" : ""}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleSel(m.id)}
                  data-testid={`message-check-${m.id}`}
                />
                <button
                  className="flex-1 min-w-0 flex items-center gap-3 text-left"
                  onClick={() => setOpen(isOpen ? null : m.id)}
                >
                  {m.deleted ? (
                    <Trash2 size={16} className="text-stone-300" />
                  ) : m.read ? (
                    <MailOpen size={16} className="text-stone-300" />
                  ) : (
                    <Mail size={16} className="text-blue-600" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`truncate ${m.read || m.deleted ? "" : "font-semibold"}`}>
                      {m.name}
                      <span className="text-stone-400 font-normal text-sm"> · {m.email}</span>
                    </p>
                    <p className="text-sm text-stone-500 truncate">{m.message || m.area || "—"}</p>
                  </div>
                  <Badge variant={m.type === "volunteer" ? "default" : "secondary"} className="hidden sm:inline-flex">
                    {m.type === "volunteer" ? "Gönüllü" : m.type === "inquiry" ? "Hizmet" : "İletişim"}
                  </Badge>
                  <span className="text-xs text-stone-400 whitespace-nowrap hidden md:inline">
                    {new Date(m.created_at).toLocaleString("tr-TR")}
                  </span>
                </button>
              </div>

              {isOpen && (
                <div className="px-5 sm:px-14 pb-5 text-sm space-y-2" data-testid={`message-detail-${m.id}`}>
                  {m.phone && <p className="flex items-center gap-2"><Phone size={13} /> {m.phone}</p>}
                  {m.district && <p><b>İlçe:</b> {m.district}</p>}
                  {m.area && <p><b>Alan:</b> {m.area}</p>}
                  <p className="whitespace-pre-line">{m.message}</p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button size="sm" variant="outline" asChild>
                      <a href={`mailto:${m.email}`}>Yanıtla</a>
                    </Button>
                    {!m.deleted && (
                      m.read ? (
                        <Button size="sm" variant="outline" onClick={() => mark(m, false)} data-testid={`mark-unread-${m.id}`}>
                          <Mail size={14} className="mr-1" />Okunmadı yap
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => mark(m, true)} data-testid={`mark-read-${m.id}`}>
                          <CheckCheck size={14} className="mr-1" />Okundu yap
                        </Button>
                      )
                    )}
                    {m.deleted && (
                      <Button size="sm" variant="outline" onClick={() => restoreOne(m)} data-testid={`restore-${m.id}`}>
                        <RotateCcw size={14} className="mr-1" />Geri al
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(m)} data-testid={`delete-message-${m.id}`}>
                      <Trash2 size={14} className="text-red-500 mr-1" />
                      {m.deleted ? "Kalıcı sil" : "Sil"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
